import { useState, useEffect, useCallback, useMemo } from "react";
import { API } from "@/App";
import { CompassCenter } from "./CompassCenter";
import { TerritoryNode } from "./TerritoryNode";
import { PathLine } from "./PathLine";
import { ActivityMarker } from "./ActivityMarker";
import axios from "axios";

// Territory configuration with visual positioning (4 nodes in diamond layout)
// Mapping: Wake Up → Self-Discovery, Burn the Map → Go Beyond, Whole → Feel Comfortable, Purposeful Paths → Walk All Paths
const TERRITORY_CONFIG = {
  "self-discovery-evolution": {
    position: { x: 50, y: 10 }, // Top center (Wake Up Quest)
    symbol: "sun",
    color: "#D4A574", // Amber/Gold
    questName: "Wake Up"
  },
  "go-beyond-the-comfort-zone": {
    position: { x: 12, y: 50 }, // Left center (Burn the Map)
    symbol: "flame", 
    color: "#C8785D", // Warm Red/Orange
    questName: "Burn the Map"
  },
  "feel-comfortable-in-your-uniqueness": {
    position: { x: 88, y: 50 }, // Right center (Whole Quest)
    symbol: "star",
    color: "#5D9E9E", // Cool Teal
    questName: "Whole"
  },
  "walk-all-the-paths-meant-for-you": {
    position: { x: 50, y: 82 }, // Bottom center (Purposeful Paths)
    symbol: "compass",
    color: "#9B7ED9", // Soft Purple
    questName: "Purposeful Paths"
  }
};

export function JourneyMap({ variant = "full" }) {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const isCompact = variant === "compact";
  
  // Memoize center position - must be before any early returns to satisfy hook rules
  const centerPosition = useMemo(() => ({ 
    x: 50, 
    y: isCompact ? 55 : 50 
  }), [isCompact]);

  const fetchMapData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/journey-map`, {
        withCredentials: true
      });
      setMapData(res.data);
    } catch (err) {
      // Silently handle errors - journey map is optional
      if (process.env.NODE_ENV === 'development') {
        console.debug('Journey map fetch failed:', err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMapData();
  }, [fetchMapData]);

  if (loading) {
    return (
      <div className="journey-map-container animate-pulse">
        <div className="h-64 bg-gradient-to-b from-[#2a1810]/20 to-[#1a0f08]/20 rounded-2xl" />
      </div>
    );
  }

  if (!mapData) return null;

  return (
    <div 
      className={`journey-map-container relative overflow-hidden rounded-2xl ${
        isCompact ? "h-52" : "h-96"
      }`}
      data-testid="journey-map"
    >
      {/* Parchment Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#2a1810] via-[#1a0f08] to-[#0B2821]">
        {/* Paper texture overlay */}
        <div 
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            mixBlendMode: "overlay"
          }}
        />
        {/* Vignette effect */}
        <div className="absolute inset-0 bg-radial-gradient pointer-events-none" 
          style={{
            background: "radial-gradient(ellipse at center, transparent 30%, rgba(11,40,33,0.6) 100%)"
          }}
        />
      </div>

      {/* Decorative corner flourishes */}
      <svg className="absolute top-2 left-2 w-8 h-8 text-[#B8956F]/30" viewBox="0 0 32 32">
        <path d="M2 2 L2 12 M2 2 L12 2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        <circle cx="6" cy="6" r="1.5" fill="currentColor" opacity="0.5"/>
      </svg>
      <svg className="absolute top-2 right-2 w-8 h-8 text-[#B8956F]/30 rotate-90" viewBox="0 0 32 32">
        <path d="M2 2 L2 12 M2 2 L12 2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        <circle cx="6" cy="6" r="1.5" fill="currentColor" opacity="0.5"/>
      </svg>
      <svg className="absolute bottom-2 left-2 w-8 h-8 text-[#B8956F]/30 -rotate-90" viewBox="0 0 32 32">
        <path d="M2 2 L2 12 M2 2 L12 2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        <circle cx="6" cy="6" r="1.5" fill="currentColor" opacity="0.5"/>
      </svg>
      <svg className="absolute bottom-2 right-2 w-8 h-8 text-[#B8956F]/30 rotate-180" viewBox="0 0 32 32">
        <path d="M2 2 L2 12 M2 2 L12 2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        <circle cx="6" cy="6" r="1.5" fill="currentColor" opacity="0.5"/>
      </svg>

      {/* SVG Layer for Paths and Elements */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {/* Path Lines connecting territories to center */}
        {mapData.territories.map((territory) => {
          const config = TERRITORY_CONFIG[territory.id];
          if (!config) return null;
          return (
            <PathLine
              key={`path-${territory.id}`}
              from={centerPosition}
              to={config.position}
              color={config.color}
              status={territory.status}
              isCompact={isCompact}
            />
          );
        })}

        {/* Activity trail markers */}
        {mapData.recent_trail.slice(0, 5).map((territoryId, index) => {
          const normalizedId = territoryId.toLowerCase().replace(/ /g, "-").replace(/'/g, "");
          const config = TERRITORY_CONFIG[normalizedId];
          if (!config) return null;
          
          // Position along the path from center to territory
          const progress = 0.3 + (index * 0.1);
          const x = 50 + (config.position.x - 50) * progress;
          const y = (isCompact ? 55 : 50) + (config.position.y - (isCompact ? 55 : 50)) * progress;
          
          return (
            <ActivityMarker
              key={`marker-${index}`}
              x={x}
              y={y}
              color={config.color}
              index={index}
            />
          );
        })}
      </svg>

      {/* Territory Nodes */}
      {mapData.territories.map((territory) => {
        const config = TERRITORY_CONFIG[territory.id];
        if (!config) return null;
        return (
          <TerritoryNode
            key={territory.id}
            territory={territory}
            config={config}
            isRecommended={mapData.recommended_territory === territory.id}
            isCompact={isCompact}
          />
        );
      })}

      {/* Central Compass */}
      <CompassCenter 
        compassDirection={mapData.compass_direction}
        overallEngagement={mapData.overall_engagement}
        streak={mapData.streak}
        isCompact={isCompact}
      />
    </div>
  );
}

export default JourneyMap;
